'use client';

import React from 'react';
import Link from 'next/link';
import clsx from 'clsx';

/**
 * Props del componente ButtonLink:
 * @param {string} href - La URL a la que el botón enlazará.
 * @param {React.ReactNode} children - El contenido del botón.
 * @param {boolean} [external=false] - Si es verdadero, el botón usará un enlace externo (<a> en lugar de <Link>).
 * @param {Function} [onClick] - Función opcional que se ejecutará al hacer clic en el botón.
 * @param {string} [className] - Clases CSS adicionales para estilizar el botón.
 * @param {string} [type] - Tipo de botón.
 * @param {boolean} [disabled] - Indica si el botón está deshabilitado.
 */
const Button = ({
  href,
  children,
  external = false,
  onClick,
  className = '',
  type = 'primary',
  disabled = false
}) => {
  const buttonClassNames = clsx(
    'transition-all duration-300 ease-out rounded-xl px-6 py-2.5 cursor-pointer w-full h-full flex items-center justify-center whitespace-nowrap font-medium text-sm',
    {
      'border border-primary-500 text-white bg-primary-500 hover:bg-primary-800 hover:border-primary-800':
        type === 'primary',
      'btn-glass-outline': type === 'outlined',
      'btn-glass-cta': type === 'secondary'
    },
    className
  );

  if (!href) {
    return (
      <button onClick={onClick} className={buttonClassNames} disabled={disabled}>
        {children}
      </button>
    );
  }

  if (external) {
    return (
      <a
        href={href}
        onClick={onClick}
        className={buttonClassNames}
        target="_blank"
        rel="noopener noreferrer"
        disabled={disabled}
      >
        {children}
      </a>
    );
  }

  return (
    <Link href={href} passHref className={buttonClassNames} disabled={disabled} onClick={onClick}>
      {children}
    </Link>
  );
};

export default Button;
